<template>
  <admin-wrapper title-translation-path="admin.appSettingsTitle" :inputs="inputs" @save="onSave">
    <template #logo.input="{dropzoneOptions}">
      <file-input-image class="pl-3" :options="dropzoneOptions" type="logo" :max-height="200" />
    </template>
    <template #favicon.input="{dropzoneOptions}">
      <file-input-image class="pl-3" :options="dropzoneOptions" type="favicon" :max-height="256" />
    </template>
    <template #login.input="{dropzoneOptions}">
      <file-input-image class="pl-3" type="theme_picture" :options="dropzoneOptions" :max-height="500" />
    </template>
    <template #basemapLayer.input="data">
      <v-col cols="3">
        <data-input v-bind="data" v-model="selectedBasemapLayerId" />
      </v-col>
    </template>
    <template #customMenus.input="data">
      <v-col cols="12">
        <v-row no-gutters class="pb-5">
          <card-title-row translation-path="admin.customMenus.navbar" :has-icon-column="false" :has-buttons="false" />
        </v-row>
        <v-row no-gutters>
          <custom-menus-draggable-list ref="custom-menus-draggable-list" v-bind="data" />
        </v-row>
      </v-col>
    </template>
    <template #keyBindings.input>
      <v-col class="key-bindings-col pt-3">
        <v-row no-gutters class="pb-5">
          <card-title-row
            translation-path="admin.keyBindings.keysBindingsConfigurationTitle"
            :has-icon-column="false"
            :has-buttons="false"
          >
            <template #subtitle>
              <italic-subheader
                translation-path="admin.keyBindings.keysBindingsConfigurationSubtitle"
                class="text-left"
              />
            </template>
          </card-title-row>
        </v-row>
        <v-row no-gutters>
          <key-bindings-settings ref="key-bindings-settings" />
        </v-row>
      </v-col>
    </template>
  </admin-wrapper>
</template>

<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import CardTitleRow from '@/components/CardTitleRow';
import CustomMenusDraggableList from '@/components/CustomMenusDraggableList';
import FileInputImage from '@/components/FileInputImage';
import DataInput from '@/components/DataInput';
import ItalicSubheader from '@/components/ItalicSubheader';
import KeyBindingsSettings from '@/components/KeyBindingsSettings';

export default {
  name: 'AppSettings',
  components: {
    AdminWrapper,
    CardTitleRow,
    CustomMenusDraggableList,
    FileInputImage,
    DataInput,
    ItalicSubheader,
    KeyBindingsSettings,
  },
  data: () => ({
    logoDeleteLoading: false,
    favionDeleteLoading: false,
    loginWidth: 800,
    loginHeight: 1050,
    images: {
      logo: {
        file: undefined,
        src: '',
        url: '',
      },
      favicon: {
        file: undefined,
        src: '',
        url: '',
      },
      themePicture: {
        file: undefined,
        src: '',
        url: '',
      },
    },
    selectedBasemapLayerId: null,
  }),
  mounted() {
    this.selectedBasemapLayerId = this.basemapLayer?.id;
    this.refreshLogoUrl();
    this.refreshFaviconUrl();
    this.refreshThemePictureUrl();
  },
  computed: {
    isUserSuperadmin: get('users/currentUser@is_superadmin'),
    isKeyBindingsEnabled: get('admin/additionalModules@KEY_BINDINGS.enabled'),
    token: get('authentication/token'),
    maxWidth: get('admin/settingsValues@custom_logo_max_width'),
    maxHeight: get('admin/settingsValues@custom_logo_max_height'),
    appName: get('admin/settingsValues@app_name'),
    layers: get('layers/layers'),
    basemapLayer: get('layers/basemapLayer'),
    basemapLayers() {
      return this.$_objectToArray(this.layers).filter(
        layer => layer.layer_scope === 'core' && layer.service_type === 'xyz'
      );
    },
    selectedBasemapLayer() {
      return this.basemapLayers.find(layer => layer.id === this.selectedBasemapLayerId);
    },
    inputs() {
      return [
        {
          name: 'app_name',
          translationPath: 'admin.fullName',
          hint: 'admin.putFullName',
          clearable: true,
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'logo',
          translationPath: 'admin.logo',
          subtitle: 'admin.logoSize',
          align: 'start',
          isCustom: true,
          subtitleParams: {
            width: this.maxWidth,
            height: this.maxHeight,
          },
          dropzoneOptions: {
            url: '/',
            urlFile: this.images.logo.src ? this.images.logo.src : this.images.logo.url,
            urlGet: this.images.logo.url,
            acceptedFiles: '.png',
            maxFiles: 1,
            autoProcessQueue: false,
            accept: (file, done) => {
              const self = this;
              this.images.logo.file = file;
              this.images.logo.src = URL.createObjectURL(file);
              const img = new Image();
              img.src = this.images.logo.src;
              img.onload = function() {
                if (this.height > self.maxHeight || this.width > self.maxWidth) {
                  done('wrongResolution');
                } else {
                  done();
                }
              };
            },
            init: function() {
              this.on('addedfile', function() {
                this.removeAllFiles(true);
              });
            },
          },
        },
        {
          name: 'favicon',
          translationPath: 'admin.favicon',
          subtitle: 'admin.faviconInfo',
          align: 'start',
          isCustom: true,
          dropzoneOptions: {
            url: '/',
            urlFile: this.images.favicon.src
              ? this.images.favicon.src
              : `${process.env.VUE_APP_API_URL}/favicon.ico?token=${this.token}`,
            urlGet: this.images.favicon.url,
            acceptedFiles: '.png,.ico',
            autoProcessQueue: false,
            accept: file => {
              this.images.favicon.file = file;
              this.images.favicon.src = URL.createObjectURL(file);
              const img = new Image();
              img.src = this.images.favicon.src;
            },
            init: function() {
              this.on('addedfile', function() {
                this.removeAllFiles(true);
              });
            },
          },
        },
        {
          name: 'login',
          translationPath: 'admin.loginImage',
          subtitle: 'admin.loginImageSubtitle',
          align: 'start',
          isCustom: true,
          subtitleParams: {
            width: 800,
            height: 1080,
          },
          dropzoneOptions: {
            url: '/',
            urlFile: this.images.themePicture.src ? this.images.themePicture.src : this.images.themePicture.url,
            urlGet: this.images.themePicture.url,
            acceptedFiles: '.jpeg,.jpg',
            maxFiles: 1,
            autoProcessQueue: false,
            accept: (file, done) => {
              const self = this;
              this.images.themePicture.file = file;
              this.images.themePicture.src = URL.createObjectURL(file);
              const img = new Image();
              img.src = this.images.themePicture.src;
              img.onload = function() {
                if (this.height === self.loginHeight || this.width > self.loginWidth) {
                  done('wrongResolution');
                } else {
                  done();
                }
              };
            },
            init: function() {
              this.on('addedfile', function() {
                this.removeAllFiles(true);
              });
            },
          },
        },
        {
          name: 'basemapLayer',
          translationPath: 'admin.basemap',
          subtitle: 'admin.basemapSubtitle',
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.basemapLayers,
          itemText: 'name',
          itemValue: 'id',
          hideDetails: true,
        },
        ...(this.isUserSuperadmin
          ? [
              {
                name: 'customMenus',
                titlePath: 'admin.customMenus.title',
                isNamedSlot: false,
                isCustom: true,
              },
              ...(this.isKeyBindingsEnabled
                ? [
                    {
                      name: 'keyBindings',
                      isNamedSlot: false,
                      isCustom: true,
                    },
                  ]
                : []),
            ]
          : []),
      ];
    },
  },
  watch: {
    appName(nV) {
      this.setDocumentTitle(nV);
    },
  },
  methods: {
    putLogo: call('admin/putLogo'),
    putFavicon: call('admin/putFavicon'),
    putThemePicture: call('admin/putThemePicture'),
    setBasemapLayer: call('layers/setBasemapLayer'),
    setDocumentTitle(name) {
      document.title = name;
    },
    async onSave() {
      if (this.images.logo.file && this.images.logo.src) {
        const formData = new FormData();
        formData.append('logo', this.images.logo.file);
        await this.putLogo(formData);
        this.refreshLogoUrl();
        this.$root.$emit('refreshLogo');
        this.images.logo.file = undefined;
        this.images.logo.src = '';
      }
      if (this.images.favicon.file && this.images.favicon.src) {
        const formData = new FormData();
        formData.append('favicon', this.images.favicon.file);
        await this.putFavicon(formData);
        this.refreshFaviconUrl();
        this.images.favicon.file = undefined;
        this.images.favicon.src = '';
      }
      if (this.images.themePicture.file && this.images.themePicture.src) {
        const formData = new FormData();
        formData.append('theme_picture', this.images.themePicture.file);
        await this.putThemePicture(formData);
        this.refreshThemePictureUrl();
        this.images.themePicture.file = undefined;
        this.images.themePicture.src = '';
      }
      if (this.isUserSuperadmin) {
        this.$refs['custom-menus-draggable-list']?.save();
        this.$refs['key-bindings-settings']?.save();
      }
      if (this.basemapLayer.id !== this.selectedBasemapLayerId) {
        this.setBasemapLayer(this.selectedBasemapLayer.id);
      }
    },
    refreshLogoUrl() {
      this.images.logo.url = `${process.env.VUE_APP_API_URL}/settings/logo?token=${this.token}?cache=${Date.now()}`;
    },
    refreshFaviconUrl() {
      this.images.favicon.url = `${process.env.VUE_APP_API_URL}/favicon.ico?token=${this.token}?cache=${Date.now()}`;
    },
    refreshThemePictureUrl() {
      this.images.themePicture.url = `${process.env.VUE_APP_API_URL}/settings/theme_picture?token=${
        this.token
      }?cache=${Date.now()}`;
    },
  },
};
</script>

<style scoped lang="scss">
.key-bindings-col {
  @include customColSize(48);
}
</style>
